import {useMutation, useQuery} from "@apollo/client";
import {useCallback, useMemo} from "react";
import toast from "react-hot-toast";

import {
  DeleteMetricsExplorerViewResult,
  GQL_METRICS_EXPLORER_VIEWS,
  GQL_METRICS_EXPLORER_VIEW_DELETE,
  GQL_METRICS_EXPLORER_VIEW_SAVE,
  MetricsExplorerViewsData,
  SaveMetricsExplorerViewResult,
} from "@/lib/queries/metricsExplorer.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {MetricsExplorerView} from "@/lib/types/metricsExplorer.ts";
import {ExplorerConfig, deserializeConfig, serializeConfig} from "@/pages/metricsExplorer/explorerConfig.ts";

interface UseSavedViews {
  views: MetricsExplorerView[];
  /** True while the list is still arriving, so a caller can tell empty from not-yet-known. */
  loading: boolean;
  saveView: (name: string, config: ExplorerConfig, id?: number) => Promise<MetricsExplorerView | null>;
  deleteView: (id: number) => Promise<boolean>;
  configOf: (view: MetricsExplorerView) => ExplorerConfig | null;
}

export const useSavedViews = (enabled: boolean): UseSavedViews => {
  const {data, refetch, loading} = useQuery<MetricsExplorerViewsData>(GQL_METRICS_EXPLORER_VIEWS, {
    skip: !enabled,
  });
  const [saveMutation] = useMutation<SaveMetricsExplorerViewResult>(GQL_METRICS_EXPLORER_VIEW_SAVE);
  const [deleteMutation] = useMutation<DeleteMetricsExplorerViewResult>(GQL_METRICS_EXPLORER_VIEW_DELETE);

  const views = useMemo(() => data?.metricsExplorerViews?.items ?? [], [data]);

  const saveView = useCallback(
    async (name: string, config: ExplorerConfig, id?: number) => {
      const {data: result} = await saveMutation({
        // The stored document is the same one the URL carries, so a saved chart and a
        // shared link never describe a chart differently.
        variables: {id, data: {name, config: JSON.parse(serializeConfig(config))}},
      });
      const response = result?.response;
      if (!response || response.status !== ResponseStatus.SUCCESS) {
        const message = response && "message" in response ? String(response.message) : "Could not save the chart.";
        toast.error(message);
        return null;
      }
      await refetch();
      toast.success(`Saved "${name}"`);
      return response.data;
    },
    [saveMutation, refetch]
  );

  const deleteView = useCallback(
    async (id: number) => {
      const {data: result} = await deleteMutation({variables: {id}});
      if (result?.response?.status !== ResponseStatus.SUCCESS) {
        toast.error("Could not delete the chart.");
        return false;
      }
      await refetch();
      return true;
    },
    [deleteMutation, refetch]
  );

  // A view saved before a config change may no longer parse; the caller shows that as a
  // broken entry rather than opening an empty chart.
  const configOf = useCallback((view: MetricsExplorerView) => deserializeConfig(JSON.stringify(view.config)), []);

  return {views, loading, saveView, deleteView, configOf};
};
